import React from "react";
import { DateTime } from "luxon";
import { getLocationTypeIcon } from "@/lib/general";
import dayjs from "dayjs";

interface CalendarItem {
  id: string | number;
  name: string;
  start_time: string;
  timezone: string;
  categories?: string[];
  type?: string;
  waytora_id?: string;
  viator_id?: string;
}

interface ColorScheme {
  background: string;
  text: string;
  icon: string;
}

const getActivityColorScheme = (activity: CalendarItem): ColorScheme => {
  // Handle special types first
  if (activity.type === "hotel") {
    return {
      background: "from-neutral-200 to-neutral-50",
      text: "text-neutral-800",
      icon: "text-neutral-600",
    };
  }

  if (activity.type === "flight") {
    return {
      background: "from-blue-100 to-blue-50",
      text: "text-blue-800",
      icon: "text-blue-600",
    };
  }

  // Get primary category and determine color scheme
  const category = activity.categories?.[0];

  if (!category) {
    return {
      background: "from-indigo-100 to-indigo-50",
      text: "text-indigo-800",
      icon: "text-indigo-600",
    };
  }

  const colorMap: Record<string, ColorScheme> = {
    "Art Gallery": {
      background: "from-purple-100 to-purple-50",
      text: "text-purple-800",
      icon: "text-purple-600",
    },
    Beach: {
      background: "from-blue-100 to-blue-50",
      text: "text-blue-800",
      icon: "text-blue-600",
    },
    Restaurant: {
      background: "from-orange-100 to-orange-50",
      text: "text-orange-800",
      icon: "text-orange-600",
    },
    Historical: {
      background: "from-amber-100 to-amber-50",
      text: "text-amber-800",
      icon: "text-amber-600",
    },
    "Street Market": {
      background: "from-lime-100 to-lime-50",
      text: "text-lime-800",
      icon: "text-lime-600",
    },
    Surfing: {
      background: "from-cyan-100 to-cyan-50",
      text: "text-cyan-800",
      icon: "text-cyan-600",
    },
    "National Park": {
      background: "from-green-100 to-green-50",
      text: "text-green-800",
      icon: "text-green-600",
    },
    Parks: {
      background: "from-green-100 to-green-50",
      text: "text-green-800",
      icon: "text-green-600",
    },
    Park: {
      background: "from-green-100 to-green-50",
      text: "text-green-800",
      icon: "text-green-600",
    },
    Nightlife: {
      background: "from-violet-100 to-violet-50",
      text: "text-violet-800",
      icon: "text-violet-600",
    },
    Shopping: {
      background: "from-emerald-100 to-emerald-50",
      text: "text-emerald-800",
      icon: "text-emerald-600",
    },
    "Architectural Wonder": {
      background: "from-rose-100 to-rose-50",
      text: "text-rose-800",
      icon: "text-rose-600",
    },
    Museums: {
      background: "from-amber-100 to-amber-50",
      text: "text-amber-800",
      icon: "text-amber-600",
    },
    Museum: {
      background: "from-amber-100 to-amber-50",
      text: "text-amber-800",
      icon: "text-amber-600",
    },
    Wildlife: {
      background: "from-green-100 to-green-50",
      text: "text-green-800",
      icon: "text-green-600",
    },
    "Amusement Park": {
      background: "from-pink-100 to-pink-50",
      text: "text-pink-800",
      icon: "text-pink-600",
    },
    "Food Market": {
      background: "from-lime-100 to-lime-50",
      text: "text-lime-800",
      icon: "text-lime-600",
    },
    "Hiking Trail": {
      background: "from-green-100 to-green-50",
      text: "text-green-800",
      icon: "text-green-600",
    },
    Clubs: {
      background: "from-violet-100 to-violet-50",
      text: "text-violet-800",
      icon: "text-violet-600",
    },
    Bar: {
      background: "from-violet-100 to-violet-50",
      text: "text-violet-800",
      icon: "text-violet-600",
    },
    Religious: {
      background: "from-indigo-100 to-indigo-50",
      text: "text-indigo-800",
      icon: "text-indigo-600",
    },
    Theatre: {
      background: "from-rose-100 to-rose-50",
      text: "text-rose-800",
      icon: "text-rose-600",
    },
    Restaurants: {
      background: "from-orange-100 to-orange-50",
      text: "text-orange-800",
      icon: "text-orange-600",
    },
    Viewpoint: {
      background: "from-blue-100 to-blue-50",
      text: "text-blue-800",
      icon: "text-blue-600",
    },
    Zoo: {
      background: "from-green-100 to-green-50",
      text: "text-green-800",
      icon: "text-green-600",
    },
    Wellness: {
      background: "from-purple-100 to-purple-50",
      text: "text-purple-800",
      icon: "text-purple-600",
    },
    "Natural Wonder": {
      background: "from-blue-100 to-blue-50",
      text: "text-blue-800",
      icon: "text-blue-600",
    },
    Stadium: {
      background: "from-orange-100 to-orange-50",
      text: "text-orange-800",
      icon: "text-orange-600",
    },
    "viator-experience": {
      background: "from-emerald-100 to-emerald-50",
      text: "text-emerald-800",
      icon: "text-emerald-600",
    },
  };

  return (
    colorMap[category] || {
      background: "from-indigo-100 to-indigo-50",
      text: "text-indigo-800",
      icon: "text-indigo-600",
    }
  );
};

interface CalendarDayProps {
  activities: CalendarItem[];
}

export const CalendarDay: React.FC<CalendarDayProps> = ({ activities }) => {
  const getActivityIcon = (activity: CalendarItem): string => {
    if (activity.type === "hotel") return "fa-bed";
    if (activity.type === "flight") return "fa-plane";

    if (activity.categories && activity.categories.length > 0) {
      return getLocationTypeIcon(activity.categories[0]!);
    }

    return "fa-location-dot";
  };

  return (
    <div className="flex-1 border-r border-neutral-100 relative min-w-[150px] p-2">
      <div className="flex flex-col space-y-2 pb-4">
        {activities.map((activity) => {
          const colorScheme = getActivityColorScheme(activity);
          const icon = getActivityIcon(activity);

          return (
            <div
              key={activity.id}
              className={`p-2.5 bg-gradient-to-br ${colorScheme.background} rounded-lg shadow-md 
                         hover:shadow-lg hover:rotate-[1.5deg] transition ease-in-out duration-500`}
            >
              <div className="pb-2 space-y-2">
                <div className="flex flex-row items-center">
                  <div
                    className={`${colorScheme.text} font-semibold text-[11.5px] 2xl:text-[12px]`}
                  >
                    {activity.name}
                  </div>
                </div>

                <div className="flex flex-row space-x-3 items-center">
                  <i className={`fas ${icon} text-xs 2xl:text-[13px] ${colorScheme.icon}`}></i>

                  <div
                    className={`${colorScheme.text} text-xs 2xl:text-[13px] rounded-md flex flex-row space-x-2 items-center`}
                  >
                    <i className={`far fa-clock ${colorScheme.icon}`}></i>
                    <p>
                      {dayjs(activity.start_time)
                        .tz(activity.timezone)
                        .format("h:mm a")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
